import React, { createElement } from 'react';
import { useData } from '../../store/DataContext';
import { AlertTriangleIcon, DownloadIcon, FileSpreadsheetIcon, PackageXIcon } from 'lucide-react';
import { isLowStockOnly, isOutOfStock } from './pharmacyUtils';
export function LowStockAlerts() {
  const {
    medicines
  } = useData();
  const out = medicines.filter(isOutOfStock);
  const low = medicines.filter(isLowStockOnly);
  const exportReport = () => {
    const rows = [...out.map(medicine => ({
      ...medicine,
      status: 'Out of stock'
    })), ...low.map(medicine => ({
      ...medicine,
      status: 'Low stock'
    }))];
    const csv = ['Drug Code,Medicine,Category,Status,Current Quantity,Reorder Level', ...rows.map(medicine => [medicine.drugCode, medicine.name, medicine.category, medicine.status, medicine.currentQuantity, medicine.reorderLevel].map(csvValue).join(','))].join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `low-stock-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-8930" data-mp="3hx7husyng44o">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between" data-id="element-8931" data-mp="3hy93tsh7eb16">
        <div data-id="element-8932" data-mp="3cyw5obkvthja">
          <h2 className="text-xl font-bold text-slate-950" data-id="element-8933" data-mp="3cz60ccgvgcvb">Low Stock Alerts</h2>
          <p className="mt-1 text-sm text-slate-500" data-id="element-8934" data-mp="3qpri1xulagrm">
            Automatic alerts appear whenever current quantity is at or below the
            medicine reorder level.
          </p>
        </div>
        <button type="button" onClick={exportReport} className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-600" data-id="element-8935" data-mp="2y8ozau85foyy">
          <FileSpreadsheetIcon className="mr-2 h-4 w-4" data-id="element-8936" data-mp="125aedh0scjeh" />
          <DownloadIcon className="mr-1.5 h-4 w-4" data-id="element-8937" data-mp="123pkltmap4ho" /> Export Excel-compatible
          CSV
        </button>
      </div>
      <AlertGroup title="Out of stock" items={out} icon={PackageXIcon} tone="red" empty="All medicines have stock on hand." data-id="element-8938" data-mp="1bvlu00tu0h17" />
      <AlertGroup title="At or below reorder level" items={low} icon={AlertTriangleIcon} tone="amber" empty="No medicines with stock on hand are at their reorder level." data-id="element-8939" data-mp="ea7mf06opzts" />
    </div>;
}
function AlertGroup({
  title,
  items,
  icon: Icon,
  tone,
  empty
}: {
  title: string;
  items: {
    id: string;
    name: string;
    currentQuantity: number;
    reorderLevel: number;
    sku: string;
  }[];
  icon: typeof AlertTriangleIcon;
  tone: 'red' | 'amber';
  empty: string;
}) {
  return <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-id="element-8940" data-mp="3p33aip31oqmk">
      <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-4" data-id="element-8941" data-mp="3k3ib6djyqg9b">
        <div className={`rounded-lg p-2 ${tone === 'red' ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-8942" data-mp="3k5l8zjrcxdol">
          <Icon className="h-4 w-4" data-id="element-8943" data-mp="3ae72ut4whfmh" />
        </div>
        <div data-id="element-8944" data-mp="3abkz74l4rk9j">
          <h3 className="font-bold text-slate-900" data-id="element-8945" data-mp="3abunmnqj1jd4">{title}</h3>
          <p className="text-sm text-slate-500" data-id="element-8946" data-mp="35g5mwh2rtwf8">
            {items.length} medicines need attention
          </p>
        </div>
      </div>
      {items.length ? <div className="divide-y divide-slate-100" data-id="element-8947" data-mp="2wde8q9iigmjw">
          {items.map(medicine => <div key={medicine.id} className="flex items-center justify-between px-5 py-4" data-id="element-8948" data-mp="2rfcq1pozyp8y">
              <div data-id="element-8949" data-mp="ul88rgqy5dlj">
                <p className="font-semibold text-slate-900" data-id="element-8950" data-mp="ulrnypp7pnvv">{medicine.name}</p>
                <p className="mt-0.5 text-sm text-slate-500" data-id="element-8951" data-mp="poriafw9yn5a">
                  {medicine.sku} · reorder at {medicine.reorderLevel}
                </p>
              </div>
              <span className={`rounded-full px-3 py-1 text-sm font-bold ${tone === 'red' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-8952" data-mp="3nhjedjnokufg">
                {medicine.currentQuantity} on hand
              </span>
            </div>)}
        </div> : <p className="px-5 py-10 text-center text-sm text-slate-500" data-id="element-8953" data-mp="2zqe37hsoqqbh">{empty}</p>}
    </section>;
}
function csvValue(value: unknown) {
  return `"${String(value).replace(/"/g, '""')}"`;
}